import {
  Alert, Box, Button, Collapse, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import clsx from 'clsx';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import App from '../../App';
import AmplifyContextProvider from '../../App/Contexts/AmplifyContext/AmplifyContextProvider';
import useMessageChannel from '../../App/Hooks/useMessageChannel';
import { CookieProvider, useCookieContext, useUserInfo } from '../../App/Hooks/useUserInfo';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  clearHostedUiStorage,
  isLocalInflight,
  SIGN_IN_ERROR_STORAGE_KEY,
  startHostedUiSignIn,
} from '../../dualChannel/hostedUi';
import isCancelledRequestError from '../../utils/isCancelledRequestError';
import { amplifyTranslateImpl } from '../amplifyI18n';
import AppCrashFallback from '../AppCrashFallback';
import SignInHeader from '../Headers/SignInHeader';
import StyledBox from './styles';

const AUTH_SESSION_TIMEOUT_MS = 5000;
const AUTH_SESSION_TIMEOUT_ERROR = 'dual_channel_auth_session_timeout';

function hasAuthTokens(session: AuthSession | null | undefined) {
  return Boolean(session?.tokens?.accessToken && session.tokens?.idToken);
}

function isAuthSessionTimeoutError(error: unknown) {
  return error instanceof Error && error.message === AUTH_SESSION_TIMEOUT_ERROR;
}

function isRetryableAuthSessionError(error: unknown) {
  return isAuthSessionTimeoutError(error) || isCancelledRequestError(error);
}

async function fetchAuthSessionWithTimeout(): Promise<AuthSession> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      fetchAuthSession({ forceRefresh: true }),
      new Promise<AuthSession>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(AUTH_SESSION_TIMEOUT_ERROR)),
          AUTH_SESSION_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export default function DualChannelAppAuthenticator() {
  // Any error left in sessionStorage by finalizeHostedUiSignInFromUrl (run before React mounted).
  const [signInError, setSignInError] = React.useState<string | null>(
    () => sessionStorage.getItem(SIGN_IN_ERROR_STORAGE_KEY),
  );
  const [isSigningIn, setIsSigningIn] = React.useState(() => isLocalInflight());
  const [expanded, setExpanded] = React.useState<boolean | null>(
    () => Boolean(sessionStorage.getItem(SIGN_IN_ERROR_STORAGE_KEY)),
  );
  const [isClearingStorage, setIsClearingStorage] = React.useState(false);

  // Register the deep-link auth-code listener. When the Electron main process delivers
  // an OAuth code (after the user signs in via the external browser), we reload the page
  // with ?code=...&state=... so that finalizeHostedUiSignInFromUrl can exchange it on
  // the next boot (before React mounts again).
  React.useEffect(() => {
    window.authAPI?.onAuthCode(({ code, state }) => {
      const url = new URL(window.location.href);
      url.searchParams.set('code', code);
      url.searchParams.set('state', state);
      window.location.href = url.toString();
    });
  }, []);

  useMessageChannel('auth::logout', React.useCallback((event: MessageEvent<{ type?: string }>) => {
    if (event.data?.type === 'auth::logout') {
      clearHostedUiStorage().then(() => { window.location.reload(); }).catch(() => undefined);
    }
  }, []));

  const authSessionQuery = useQuery<AuthSession | null>({
    queryKey: ['dual-channel-auth-session'],
    queryFn: async () => {
      const session = await fetchAuthSessionWithTimeout();
      return hasAuthTokens(session) ? session : null;
    },
    // After an overnight sleep or network wake-up, Amplify's forced refresh can
    // timeout/cancel even though Cognito cookies and refresh tokens are still valid.
    // Keep those cases in the "checking auth" state and retry instead of showing
    // the login screen, otherwise the live app never mounts and no websocket opens.
    retry(_failureCount, error) {
      return isRetryableAuthSessionError(error);
    },
    retryDelay(failureCount) {
      return Math.min(1000 * 2 ** failureCount, 30000);
    },
    refetchOnWindowFocus: true,
  });

  React.useEffect(() => {
    if (authSessionQuery.error && !isRetryableAuthSessionError(authSessionQuery.error)) {
      console.error('Unable to refresh dual channel auth session', authSessionQuery.error);
    }
  }, [authSessionQuery.error]);

  const handleSignIn = React.useCallback(async () => {
    setIsSigningIn(true);
    setSignInError(null);
    try {
      await startHostedUiSignIn();
    } catch (err) {
      setIsSigningIn(false);
      const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      setSignInError(message);
      setExpanded(true);
    }
  }, []);

  const handleSignInInApp = React.useCallback(async () => {
    setIsSigningIn(true);
    setSignInError(null);
    try {
      await startHostedUiSignIn({ inElectron: true });
    } catch (err) {
      setIsSigningIn(false);
      const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      setSignInError(message);
    }
  }, []);

  const handleClearStorage = React.useCallback(async () => {
    setIsClearingStorage(true);
    setSignInError(null);
    try {
      sessionStorage.removeItem(SIGN_IN_ERROR_STORAGE_KEY);
      await clearHostedUiStorage();
    } finally {
      setIsClearingStorage(false);
      setIsSigningIn(false);
      setExpanded(false);
    }
  }, []);

  // When the login screen is visible after a tokenless auth session result, the
  // Cognito session cookies may still be valid. Poll with a cheap (no-network) session
  // read; if tokens appear, reload so the full auth flow re-runs from scratch.
  // React Query keeps data empty while retrying, so include isFetching while no
  // accepted session is mounted to avoid briefly treating a retryable refresh
  // failure as a definitive logged-out state.
  const hasSession = hasAuthTokens(authSessionQuery.data);
  const [hasAcceptedSession, setHasAcceptedSession] = React.useState(false);
  React.useEffect(() => {
    if (hasSession) {
      setHasAcceptedSession(true);
      return;
    }

    if (authSessionQuery.isSuccess && !authSessionQuery.isFetching) {
      setHasAcceptedSession(false);
    }
  }, [authSessionQuery.isFetching, authSessionQuery.isSuccess, hasSession]);

  const shouldKeepAppMounted = hasSession || (hasAcceptedSession && authSessionQuery.isFetching);
  const isCheckingAuthSession = !shouldKeepAppMounted && (authSessionQuery.isLoading || authSessionQuery.isFetching);
  const isShowingLoginScreen = !isCheckingAuthSession && !shouldKeepAppMounted;
  React.useEffect(() => {
    if (!isShowingLoginScreen) return undefined;
    const id = setInterval(async () => {
      try {
        const session = await fetchAuthSession();
        if (hasAuthTokens(session)) {
          console.error('[DualChannelAppAuthenticator] Valid session detected on login screen — reloading');
          window.location.reload();
        }
      } catch {
        // ignore — we'll try again next interval
      }
    }, 5_000);

    return () => clearInterval(id);
  }, [isShowingLoginScreen]);

  if (isCheckingAuthSession) {
    return (
      <StyledBox>
        <Box className="dual-channel-auth-loading">
          <LoadingBackdrop />
        </Box>
      </StyledBox>
    );
  }

  if (shouldKeepAppMounted) {
    return (
      <ErrorBoundary
        FallbackComponent={AppCrashFallback}
        onError={(error) => console.error('[DualChannelAppAuthenticator] App-level crash', error)}
      >
        <StyledBox>
          <CookieProvider>
            <AmplifyContextProvider signOut={undefined} user={undefined}>
              <DualChannelUserInfoBridge />
              <App />
            </AmplifyContextProvider>
          </CookieProvider>
        </StyledBox>
      </ErrorBoundary>
    );
  }

  return (
    <StyledBox>
      <Box className="dual-channel-sign-in">
        <Box className="login-card">
          <SignInHeader />
          <Box className="login-actions">
            <Box textAlign="center" marginBottom={4}>
              <Button
                variant="contained"
                onClick={handleSignIn}
                disabled={isSigningIn}
                sx={{ width: 250 }}
              >
                {amplifyTranslateImpl('login.sign-in')}
              </Button>
            </Box>

            <Box className={clsx('trouble-box', { expanded: expanded !== false })}>
              <Button
                variant="text"
                onClick={() => setExpanded(!expanded)}
                className="trouble-toggle"
              >
                {amplifyTranslateImpl('login.having-issues')}
              </Button>

              {isClearingStorage && <LoadingBackdrop />}

              <Collapse in={expanded !== false} timeout="auto" unmountOnExit>
                <Box className="trouble-content">
                  <Box className="trouble-inner">
                    {signInError ? (
                      <Alert
                        severity="error"
                        onClose={() => {
                          sessionStorage.removeItem(SIGN_IN_ERROR_STORAGE_KEY);
                          setSignInError(null);
                        }}
                      >
                        <Typography variant="body2">
                          {amplifyTranslateImpl('login.sync-error')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {signInError === 'login.sync-error-unknown'
                            ? amplifyTranslateImpl('login.sync-error-unknown')
                            : signInError}
                        </Typography>
                      </Alert>
                    ) : (
                      <Typography variant="body2">
                        {amplifyTranslateImpl('login.trouble-text')}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      className="clear-storage-button"
                      onClick={handleClearStorage}
                      disabled={isClearingStorage}
                    >
                      {amplifyTranslateImpl('login.clear-storage')}
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      variant="text"
                      className="trouble-toggle"
                      onClick={handleSignInInApp}
                      disabled={isSigningIn}
                    >
                      {amplifyTranslateImpl('login.sign-in-in-app')}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </Box>
    </StyledBox>
  );
}

function DualChannelUserInfoBridge() {
  const { token } = useCookieContext();
  const userInfoQuery = useUserInfo(token, { enabled: !!token });
  const sendUserInfo = useMessageChannel('live:user-info');

  const payload = React.useMemo(() => {
    const userInfo = userInfoQuery.data?.getUserInfo;
    if (!userInfo) return null;

    return {
      userId: userInfo.id,
      email: userInfo.email,
      organisationId: userInfo.organisationId,
      role: userInfo.role,
      isStaff: userInfo.isStaff,
      displayName: userInfo.displayName,
    };
  }, [userInfoQuery.data]);

  const sendCurrentUserInfo = React.useCallback(() => {
    if (payload) {
      sendUserInfo(payload);
    }
  }, [payload, sendUserInfo]);

  useMessageChannel('live:user-info', React.useCallback((event: MessageEvent<{ type?: string }>) => {
    if (event.data?.type === 'get-init-state') {
      sendCurrentUserInfo();
    }
  }, [sendCurrentUserInfo]));

  React.useEffect(() => {
    sendCurrentUserInfo();
  }, [sendCurrentUserInfo]);

  return null;
}
